import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import officialLogo from "../../assets/logo/the-first-noncarbon-artist.png";
import WheelPicker from "../WheelPicker/WheelPicker";
import About from "../../pages/About/About";
import "./Sidebar.css";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const RIPPLE_GROWTH_MS = 1200;
const WHEEL_ITEM_HEIGHT = 40;
const BASE_VISIBLE_GROUPS = 7;
const MAX_VISIBLE_GROUPS = 10;

const getVisibleGroupCount = (groupCount) => {
  if (groupCount <= BASE_VISIBLE_GROUPS) {
    return BASE_VISIBLE_GROUPS;
  }

  const extraSlots = Math.min(
    MAX_VISIBLE_GROUPS - BASE_VISIBLE_GROUPS,
    Math.ceil((groupCount - BASE_VISIBLE_GROUPS) / 3),
  );

  return BASE_VISIBLE_GROUPS + extraSlots;
};

const Sidebar = ({
  groups,
  currentIndex,
  onIndexChange,
  layoutVars,
  preloadedTeamMembers,
  teamError,
  preloadedNews,
  newsError,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showAbout, setShowAbout] = useState(false);

  // Single tracking ripple — exact RippleButton pattern on the whole sidebar
  const sidebarRef = useRef(null);
  const [ripple, setRipple] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const isHoveredRef = useRef(false);
  const hoverStartTimeRef = useRef(0);
  const rippleFrameRef = useRef(null);
  const groupCount = groups?.length || 0;
  const visibleGroupCount = getVisibleGroupCount(groupCount);
  const desktopMinHeight = 136 + visibleGroupCount * WHEEL_ITEM_HEIGHT;
  const mobileVisibleGroupCount = Math.max(6, visibleGroupCount - 1);
  const mobileMinHeight = 112 + mobileVisibleGroupCount * WHEEL_ITEM_HEIGHT;
  const resolvedLayoutVars = {
    ...layoutVars,
    "--sidebar-height-px": `${desktopMinHeight}px`,
    "--sidebar-height-mobile-px": `${mobileMinHeight}px`,
  };

  const stopRippleGrowth = useCallback(() => {
    if (rippleFrameRef.current !== null) {
      cancelAnimationFrame(rippleFrameRef.current);
      rippleFrameRef.current = null;
    }
  }, []);

  const getRippleMetrics = useCallback((event) => {
    const rect = sidebarRef.current.getBoundingClientRect();
    const baseSize = Math.max(rect.width * 2.05, 170);
    const maxSize = Math.max(
      Math.hypot(rect.width, rect.height) * 1.25,
      baseSize,
    );

    return {
      x: clamp(event.clientX - rect.left, 0, rect.width),
      y: clamp(event.clientY - rect.top, 0, rect.height),
      baseSize,
      maxSize,
    };
  }, []);

  const growRipple = useCallback((timestamp) => {
    if (!isHoveredRef.current) {
      rippleFrameRef.current = null;
      return;
    }

    const elapsed = timestamp - hoverStartTimeRef.current;
    const progress = Math.min(elapsed / RIPPLE_GROWTH_MS, 1);
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    setRipple((prev) => {
      if (!prev || prev.isLeaving) return prev;

      return {
        ...prev,
        size: prev.baseSize + (prev.maxSize - prev.baseSize) * easedProgress,
      };
    });

    if (progress < 1) {
      rippleFrameRef.current = requestAnimationFrame(growRipple);
    } else {
      rippleFrameRef.current = null;
    }
  }, []);

  const createRipple = useCallback(
    (event) => {
      if (isHovered || !sidebarRef.current) return;
      setIsHovered(true);
      isHoveredRef.current = true;
      stopRippleGrowth();
      hoverStartTimeRef.current = performance.now();
      const { x, y, baseSize, maxSize } = getRippleMetrics(event);
      setRipple({
        x,
        y,
        size: baseSize,
        baseSize,
        maxSize,
        key: Date.now(),
        isLeaving: false,
      });
    },
    [getRippleMetrics, isHovered, stopRippleGrowth],
  );

  useEffect(() => {
    if (!isHovered || !ripple || ripple.isLeaving) return undefined;

    rippleFrameRef.current = requestAnimationFrame(growRipple);

    return () => {
      stopRippleGrowth();
    };
  }, [growRipple, isHovered, ripple, stopRippleGrowth]);

  const removeRipple = useCallback(
    (event) => {
      if (!sidebarRef.current) return;
      setIsHovered(false);
      isHoveredRef.current = false;
      stopRippleGrowth();
      const { x, y, baseSize, maxSize } = getRippleMetrics(event);
      setRipple((prev) =>
        prev
          ? {
              ...prev,
              x,
              y,
              baseSize,
              maxSize,
              isLeaving: true,
            }
          : {
              x,
              y,
              size: baseSize,
              baseSize,
              maxSize,
              key: Date.now(),
              isLeaving: true,
            },
      );
    },
    [getRippleMetrics, stopRippleGrowth],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!sidebarRef.current || !isHovered || !ripple) return;
      const { x, y } = getRippleMetrics(event);
      setRipple((prev) => ({
        ...prev,
        x,
        y,
      }));
    },
    [getRippleMetrics, isHovered, ripple],
  );

  useEffect(() => {
    const clearRipple = () => {
      setIsHovered(false);
      isHoveredRef.current = false;
      stopRippleGrowth();
      setRipple(null);
    };

    window.addEventListener("blur", clearRipple);

    return () => {
      window.removeEventListener("blur", clearRipple);
    };
  }, [stopRippleGrowth]);

  return (
    <>
      <div className="sidebar-shell" style={resolvedLayoutVars}>
        {!isOpen && (
          <button
            className="sidebar-open-btn"
            onClick={() => setIsOpen(true)}
            aria-label="Open navigation"
          >
            <span />
          </button>
        )}

        <aside
          ref={sidebarRef}
          className={`sidebar${isOpen ? "" : " sidebar--hidden"}`}
          onPointerEnter={createRipple}
          onPointerLeave={removeRipple}
          onPointerMove={handlePointerMove}
        >
          <div className="sidebar-content">
            <button
              className="sidebar-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close navigation"
            >
              ×
            </button>

            {/* ── Top: logo ─────────────────────────────── */}
            <div className="sidebar-top">
              <Link
                to="/"
                className="sidebar-logo-link"
                aria-label="Go to gallery"
              >
                <img
                  src={officialLogo}
                  alt="The First NonCarbon Artist"
                  className="sidebar-logo-img"
                />
              </Link>
            </div>

            {/* ── Middle: WheelPicker ────────────────────── */}
            <div className="sidebar-middle">
              <WheelPicker
                groups={groups}
                currentIndex={currentIndex}
                onIndexChange={onIndexChange}
                visibleCount={visibleGroupCount}
              />
            </div>

            {/* ── Bottom: About ─────────────────────────── */}
            <div className="sidebar-bottom">
              <button
                className={`sidebar-about-btn${showAbout ? " sidebar-about-btn--active" : ""}`}
                onClick={() => setShowAbout(true)}
              >
                About
              </button>
            </div>
          </div>

          <AnimatePresence>
            {ripple && (
              <motion.span
                key={ripple.key}
                className="sidebar-ripple"
                style={{
                  width: ripple.size,
                  height: ripple.size,
                  left: ripple.x,
                  top: ripple.y,
                  x: "-50%",
                  y: "-50%",
                }}
                initial={{ scale: 0, opacity: 1 }}
                animate={{
                  scale: ripple.isLeaving ? 0 : 1,
                  x: "-50%",
                  y: "-50%",
                }}
                exit={{ scale: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                onAnimationComplete={() => {
                  if (ripple.isLeaving) setRipple(null);
                }}
              />
            )}
          </AnimatePresence>
        </aside>
      </div>

      {showAbout && (
        <div
          className="about-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="About"
        >
          <button
            className="about-overlay-close"
            onClick={() => setShowAbout(false)}
            aria-label="Close About"
          >
            <span className="about-overlay-close__glyph" aria-hidden="true">
              ×
            </span>
          </button>
          <About
            preloadedTeamMembers={preloadedTeamMembers}
            teamError={teamError}
            preloadedNews={preloadedNews}
            newsError={newsError}
          />
        </div>
      )}
    </>
  );
};

export default Sidebar;
